import React, { useEffect, useState } from "react";
import {
  ProgressIndicator,
  ProgressStep,
  Button,
  Row,
  Column,
  Slider,
  ComposedModal, ModalHeader, ModalBody, ModalFooter
} from "carbon-components-react";
import { useNavigate } from "react-router";
import ClaycoForm from "./form_elements/clayco-form";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import useMsalApi from "./use-msal-api";
import {
  updateEnrollments,
  updateStep,
} from "../redux/actions/enrollments-actions";
import SubmitButton from "./form_elements/submit-button";
import { EnrollmentType } from "./enums/enrollment-type";
import { useFormState } from "react-hook-form";
import SaveLater from "./save-later";
import confirm from "../forms/confirm";
import Footer from "./footer";
import StartOverButton from "./form_elements/start-over-button";
import Initialize from "./initilize";

const StepContent = ({
  children,
  pageStep,
  schema,
  defaultValues,
  onSubmit,
  onSave,
  onError,
  onReset,
  disabled,
}) => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [confirmSubmitting, setConfirmSubmitting] = useState();

  const [lastVisited, setLastVisited] = useState(0);
  const [currentStep, setCurrentStep] = useState(pageStep);
  const enrollments = useSelector((s) => s.enrollments);
  const dispatch = useDispatch();
  const [callApi] = useMsalApi();

  useEffect(() => {
    if (Array.isArray(enrollments)) {
      Initialize(callApi, dispatch, navigate);
    } else {
      console.log("Going to Current Step", enrollments.currentStep);
      setLastVisited(enrollments.currentStep);
      if (enrollments.currentStep < pageStep) {
        setCurrentStep(enrollments.currentStep);
      }
    }
  }, [enrollments]);

  const validationSchema = schema ? schema : Yup.object();    
  
  const submit = async (formData, e) => {
    if (onSubmit) {
      setSubmitting(true);
      if (onSubmit) await onSubmit(formData);

      if (currentStep === 2 || enrollments.currentStep < currentStep + 1) {
        dispatch(updateStep(currentStep + 1));
        await callApi(
          `Enrollments/${enrollments.id}/Step/${currentStep + 1}`,
          "PATCH"
        );
      }
      setSubmitting(false);
    }

    next();
  };

  // Save the user's progress so they can continue later. This persists the
  // current page's data (via the page's own save handler) but never advances
  // the step or runs the final submission to external systems. On the Review
  // step there is nothing to persist — onSubmit there is the final submit — so
  // we skip persisting and simply let the user leave the flow.
  const saveForLater = async (formData) => {
    if (onSave) {
      await onSave(formData);
    } else if (onSubmit && currentStep !== steps.length - 1) {
      await onSubmit(formData);
    }
  };

  const error = (errors, e) => {
    console.log("Form Submission Error", errors);
    if (onError) onError(errors, e);
  };

  const change = (newStep) => {
    setCurrentStep(newStep);
  };
  const next = () => {
    let nextStep = currentStep + 1;
    if (nextStep <= steps.length - 1) {
      if(currentStep === 2){
        setLastVisited(nextStep);
      }
      setCurrentStep(nextStep);

    }
  };

  const back = () => {
    let nextStep = currentStep - 1;
    if (nextStep <= steps.length - 1) {
      setCurrentStep(nextStep);
    }
  };

  useEffect(() => {
    navigate(steps.find((step) => step.index === currentStep).url);
  }, [currentStep]);


  const steps = [
    { index: 0, url: "/", label: "Start", description: "Start" },
    {
      index: 1,
      url: "/Employee",
      label: "Emp. Data",
      description: "Enter Employee Data",
    },
    {
      index: 2,
      url: "/Dependents",
      label: "Dependents",
      description: "Enter Dependent Data",
    },
    {
      index: 3,
      url: "/Medical",
      label: "Medical",
      description: "Select Medical Plan",
    },
    {
      index: 4,
      url: "/Dental",
      label: "Dental",
      description: "Select Dental Plan",
    },
    {
      index: 5,
      url: "/Vision",
      label: "Vision",
      description: "Select Vision Plan",
    },
    {
      index: 6,
      url: "/Disability",
      label: "Disability",
      description: "Review Disability",
    },
    {
      index: 7,
      url: "/LifeInsurance",
      label: "Life Ins.",
      description: "Select Life Insurance",
    },
    { index: 8, url: "/FSA", label: "FSA", description: "Select FSA Plans" },
    { index: 9, url: "/Review", label: "Review", description: "Review" },
  ];

  const confirmReset = async () => {
    const result = await confirm(`No Changes will be saved on this page.`, {
      title: "Are you sure you want to clear changes? ",
      backButton: "Back",
    });
    if (result) onReset();
  };

  const confirmSubmit = async () => {
    setConfirmSubmitting(false);
    setSubmitting(true);
    submit();
    setSubmitting(false);
  }
  const handleClose = () => {      
    setConfirmSubmitting(false); 
  }

  return (
    <ClaycoForm
      schema={validationSchema}
      defaultValues={defaultValues}
      onSubmit={submit}
      onError={error}
    >
      <Row narrow>
        <Column sm={{ span: 0 }} lg={{ span: 8 }} className="p-0">
          <ProgressIndicator
            currentIndex={currentStep}
            className="mb-3"
            onChange={change}
          >
            {steps.map((step) => (
              <ProgressStep
                key={step.index}
                label={step.label}
                description={step.description}
                index={step.index}
                disabled={step.index > lastVisited}
              />
            ))}
          </ProgressIndicator>
        </Column>

        <Column className="progress-slider-container" lg={{ span: 0 }}>
          <Slider
            labelText={`Step ${currentStep + 1}/${steps.length}`}
            min={0}
            max={steps.length - 1}
            stepMultiplier={1}
            value={currentStep}
            disabled={true}
          />{" "}
        </Column>
      </Row>
      {children}
      <Row>
        <Column>
          <div className="mt-5">
            {currentStep > 0 ? (
              <Button kind="ghost" onClick={back}>
                Back
              </Button>
            ) : (
              <></>
            )}
            {currentStep === steps.length - 1 ? (
              <SubmitButton type="button" onClick={() => setConfirmSubmitting(true)} submitting={submitting}>Submit</SubmitButton>
            ) : (
              <SubmitButton submitting={submitting}>Continue</SubmitButton>
            )}
            {currentStep === steps.length - 1 ? (
              <StartOverButton/>
            ) : (
              <></>
            )}

            {}
            {currentStep > 0 ? (
              <span className="mb-0 float-right">
                <SaveLater onSave={saveForLater} />
              </span>
            ) : (
              <></>
            )}

            <div className="mb-0 pt-4 text-right">
              <hr />
              {currentStep === 3 ||
              currentStep === 4 ||
              currentStep === 5 ||
              currentStep === 7 ||
              currentStep === 8 ? (
                <>
                  {" "}
                  <Button
                    className="text-placeholder"
                    kind="ghost"
                    onClick={confirmReset}
                    disabled={disabled}
                  >
                    Clear changes
                  </Button>
                </>
              ) : (
                <></>
              )}
            </div>
          </div>{" "}
        </Column>
      </Row>{" "}
      <div>
        {enrollments?.deniedMessage ? 
            <Footer enrollments={enrollments} />
         : 
          <></>
        }
      </div>

      <ComposedModal 
          size="xs"
          open={confirmSubmitting} 
                onClose={handleClose}
                preventCloseOnClickOutside 
            >
                <ModalHeader title={"Are you ready to submit?"} closeModal={handleClose} />
                <ModalBody aria-label="Body">
                  <p>
                  Once submitted, you will need to contact the benefits team to make additional changes.
                  </p><br />
                </ModalBody>
                <ModalFooter>
                    <Button kind="secondary" disabled={submitting} onClick={handleClose}>Cancel</Button>
                    <SubmitButton submitting={submitting}>Submit</SubmitButton>
                </ModalFooter>
      </ComposedModal>


    </ClaycoForm>
    
  );
};

export default StepContent;
